import { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Grid, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Dialog, IconButton, Chip, Avatar, Divider, InputAdornment, CircularProgress, Snackbar,
  Autocomplete, Tabs, Tab, Menu as MuiMenu, MenuItem as MuiMenuItem, ListItemIcon, ListItemText,
  Badge, Card, CardContent, Grow, Fade, InputBase, Slide, useMediaQuery, useTheme, Checkbox, FormControlLabel,
  Alert, AlertTitle, Tooltip, Popover
} from "@mui/material";
import {
  Add, Save, Cancel, Delete, Visibility, Download, MoreVert, Search, Close,
  AttachMoney, Person, Business, Receipt, Payment, CheckCircle, Edit, Warning, Info, Error,
  AddCircle, HelpOutline, Inventory, LocalOffer, DateRange, AttachMoney as MoneyIcon,
  TrendingUp, Warning as WarningIcon, CheckCircle as CheckCircleIcon
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import frLocale from "date-fns/locale/fr";
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, serverTimestamp, Timestamp, setDoc, getDoc } from "firebase/firestore";
import { db } from "../../Firebase/Config";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Fournisseur from '../../Services/Fournisseur';


// ==================== SERVICES ====================
const StockService = {
  getAll: async (restaurantId) => {
    try {
      const q = query(collection(db, "stocks"), where("restaurantId", "==", restaurantId));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting stocks:", error);
      return [];
    }
  },
  
  // Add new stock item
  addStock: async (restaurantId, stockData) => {
    try {
      const stockRef = collection(db, "stocks");
      const newStock = {
        ...stockData,
        restaurantId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        date: Timestamp.now(),
        isComposite: false,
        isProduitIntermediaire: false
      };
      const docRef = await addDoc(stockRef, newStock);
      return { id: docRef.id, ...newStock };
    } catch (error) {
      console.error("Error adding stock:", error);
      throw error;
    }
  },
  
  // Find or create stock item
  findOrCreateStock: async (restaurantId, productName, unit, quantity, pricePerUnit) => {
    try {
      // First try to find existing stock
      const q = query(
        collection(db, "stocks"),
        where("restaurantId", "==", restaurantId),
        where("productName", "==", productName),
        where("unit", "==", unit)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        // Existing stock found
        const existingDoc = snapshot.docs[0];
        const existingStock = existingDoc.data();
        return { id: existingDoc.id, ...existingStock, isNew: false };
      } else {
        // Create new stock
        const newStock = {
          productName: productName,
          pricePerUnit: parseFloat(pricePerUnit).toFixed(3),
          quantity: parseFloat(quantity),
          unit: unit,
          restaurantId: restaurantId,
          date: Timestamp.now(),
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          lastPurchasePrice: parseFloat(pricePerUnit).toFixed(3),
          lastPurchaseDate: Timestamp.now(),
          isComposite: false,
          isProduitIntermediaire: false
        };
        
        const docRef = await addDoc(collection(db, "stocks"), newStock);
        return { id: docRef.id, ...newStock, isNew: true };
      }
    } catch (error) {
      console.error("Error finding/creating stock:", error);
      throw error;
    }
  },
  
  // Update stock with weighted average price and update product costs
  updateStockWithPurchase: async (restaurantId, productName, unit, purchasedQuantity, purchasePricePerUnit) => {
    try {
      // Find existing stock for this product with same unit
      const stocksRef = collection(db, "stocks");
      const q = query(
        stocksRef, 
        where("restaurantId", "==", restaurantId),
        where("productName", "==", productName),
        where("unit", "==", unit)
      );
      const snapshot = await getDocs(q);
      
      const purchasedQty = parseFloat(purchasedQuantity);
      const purchasedPrice = parseFloat(purchasePricePerUnit);
      const purchaseTotal = purchasedQty * purchasedPrice;
      
      let newQuantity, newPricePerUnit;
      let oldQty = 0, oldPrice = 0;
      
      if (!snapshot.empty) {
        // Existing stock - update with weighted average
        const existingDoc = snapshot.docs[0];
        const existingStock = existingDoc.data();
        oldQty = parseFloat(existingStock.quantity);
        oldPrice = parseFloat(existingStock.pricePerUnit);
        const oldTotal = oldQty * oldPrice;
        
        // Calculate new values using weighted average
        newQuantity = oldQty + purchasedQty;
        const newTotalValue = oldTotal + purchaseTotal;
        newPricePerUnit = newTotalValue / newQuantity;
        
        // Update stock with 3 decimal places
        await updateDoc(existingDoc.ref, {
          quantity: newQuantity,
          pricePerUnit: parseFloat(newPricePerUnit).toFixed(3),
          date: Timestamp.now(),
          updatedAt: Timestamp.now(),
          lastPurchasePrice: parseFloat(purchasedPrice).toFixed(3),
          lastPurchaseDate: Timestamp.now()
        });
      } else {
        // New product - create stock entry
        newQuantity = purchasedQty;
        newPricePerUnit = purchasedPrice;
        
        const newStockRef = doc(collection(db, "stocks"));
        await setDoc(newStockRef, {
          productName: productName,
          pricePerUnit: parseFloat(purchasedPrice).toFixed(3),
          quantity: purchasedQty,
          unit: unit,
          restaurantId: restaurantId,
          date: Timestamp.now(),
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          lastPurchasePrice: parseFloat(purchasedPrice).toFixed(3),
          lastPurchaseDate: Timestamp.now()
        });
      }
      
      // AFTER updating stock, update all products that use this stock ingredient
      await StockService.updateProductCostsForIngredient(restaurantId, productName, parseFloat(newPricePerUnit).toFixed(3));
      
      return { 
        success: true, 
        message: "Stock mis à jour", 
        oldQty, 
        oldPrice, 
        newQuantity, 
        newPricePerUnit: parseFloat(newPricePerUnit).toFixed(3)
      };
    } catch (error) {
      console.error("Error updating stock:", error);
      return { success: false, message: error.message };
    }
  },
  
  // Update all product costs that use this ingredient
  updateProductCostsForIngredient: async (restaurantId, ingredientName, newPricePerUnit) => {
    try {
      // Get all products that use this ingredient
      const productsRef = collection(db, "products");
      const q = query(
        productsRef,
        where("restaurantId", "==", restaurantId)
      );
      const productsSnapshot = await getDocs(q);
      
      const productsToUpdate = [];
      productsSnapshot.docs.forEach(doc => {
        const product = { id: doc.id, ...doc.data() };
        // Check if this product uses the ingredient
        const usesIngredient = product.ingredients?.some(
          ing => ing.stockName === ingredientName
        );
        if (usesIngredient) {
          productsToUpdate.push(product);
        }
      });
      
      // Update each product's cost price
      for (const product of productsToUpdate) {
        await StockService.recalculateProductCost(product, restaurantId);
      }
      
      console.log(`Updated ${productsToUpdate.length} products using ingredient: ${ingredientName}`);
      return productsToUpdate.length;
    } catch (error) {
      console.error("Error updating product costs:", error);
      return 0;
    }
  },
  
  // Recalculate a single product's cost based on current stock prices
  recalculateProductCost: async (product, restaurantId) => {
    try {
      let totalCost = 0;
      
      if (product.ingredients && product.ingredients.length > 0) {
        for (const ingredient of product.ingredients) {
          // Get current stock price for this ingredient
          const stockQuery = query(
            collection(db, "stocks"),
            where("productName", "==", ingredient.stockName),
            where("restaurantId", "==", restaurantId)
          );
          const stockSnapshot = await getDocs(stockQuery);
          
          if (!stockSnapshot.empty) {
            const stockData = stockSnapshot.docs[0].data();
            const ingredientCost = ingredient.quantity * parseFloat(stockData.pricePerUnit);
            totalCost += ingredientCost;
          }
        }
      }
      
      // Update product with new cost price (3 decimal places)
      const productRef = doc(db, "products", product.id);
      await updateDoc(productRef, {
        costPrice: parseFloat(totalCost).toFixed(3),
        updatedAt: Timestamp.now()
      });
      
      console.log(`Recalculated cost for ${product.name}: ${totalCost.toFixed(3)}`);
      return parseFloat(totalCost).toFixed(3);
    } catch (error) {
      console.error(`Error recalculating cost for product ${product.name}:`, error);
      return 0;
    }
  },
  
  // Get all products for dropdown
  getAllProducts: async (restaurantId) => {
    try {
      const q = query(collection(db, "products"), where("restaurantId", "==", restaurantId));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting products:", error);
      return [];
    }
  }
};

const FactureService = {
  getAll: async (restaurantId) => {
    try {
      const q = query(collection(db, "factures"), where("restaurantId", "==", restaurantId));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const dateA = a.date?.toDate?.() || new Date(a.date);
          const dateB = b.date?.toDate?.() || new Date(b.date);
          return dateB - dateA;
        });
    } catch (error) {
      console.error("Error getting factures:", error);
      return [];
    }
  },
  
  create: async (restaurantId, data) => {
    try {
      const docRef = await addDoc(collection(db, "factures"), { 
        ...data, 
        restaurantId, 
        createdAt: serverTimestamp(), 
        updatedAt: serverTimestamp() 
      });
      return { id: docRef.id, ...data };
    } catch (error) {
      console.error("Error creating facture:", error);
      throw error;
    }
  },
  
  update: async (restaurantId, id, data) => {
    try {
      await updateDoc(doc(db, "factures", id), { 
        ...data, 
        updatedAt: serverTimestamp() 
      });
      return { id, ...data };
    } catch (error) {
      console.error("Error updating facture:", error);
      throw error;
    }
  },
  
  delete: async (restaurantId, id) => {
    try {
      await deleteDoc(doc(db, "factures", id));
      return true;
    } catch (error) {
      console.error("Error deleting facture:", error);
      throw error;
    }
  }
};

// ==================== COMPONENTS ====================
const StyledDialog = ({ open, onClose, title, icon: Icon, maxWidth = "sm", actions, children }) => {
  const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));
  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth={maxWidth} fullWidth TransitionComponent={Grow}>
      <Box sx={{ bgcolor: "#1e293b", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.1)" }}><Icon /></Avatar>
        <Typography variant="h6" sx={{ color: "white", flex: 1 }}>{title}</Typography>
        <IconButton onClick={onClose} sx={{ color: "white" }}><Close /></IconButton>
      </Box>
      <Box sx={{ p: 3, maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>{children}</Box>
      {actions && <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", bgcolor: "#fafafa" }}>{actions}</Box>}
    </Dialog>
  );
};

const InfoCard = ({ title, value, icon: Icon, color, subtitle, tooltip }) => (
  <Tooltip title={tooltip} arrow placement="top">
    <Card sx={{ borderRadius: 3, transition: "0.3s", height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {title}
              <HelpOutline sx={{ fontSize: 14, color: "#94a3b8", cursor: "help" }} />
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
          </Box>
          <Avatar sx={{ bgcolor: `rgba(${color === "#3b82f6" ? "59,130,246" : color === "#10b981" ? "16,185,129" : color === "#f59e0b" ? "245,158,11" : "239,68,68"}, 0.1)`, color }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  </Tooltip>
);

// Format number with 3 decimal places
const formatNumber = (n) => {
  if (n === null || n === undefined) return '0,000';
  const num = parseFloat(n);
  if (isNaN(num)) return '0,000';
  return num.toFixed(3).replace('.', ',');
};

// Parse number from French format (comma as decimal separator)
const parseNumber = (value) => {
  if (!value && value !== 0) return 0;
  const stringValue = value.toString().replace(',', '.');
  return parseFloat(stringValue) || 0;
};

// Info Alert Component
const InfoAlert = ({ message, severity = "info", onClose }) => (
  <Alert 
    severity={severity} 
    onClose={onClose}
    sx={{ mb: 2, borderRadius: 2 }}
    iconMapping={{
      info: <Info />,
      warning: <WarningIcon />,
      success: <CheckCircleIcon />
    }}
  >
    <AlertTitle>{severity === "info" ? "💡 Astuce" : severity === "warning" ? "⚠️ Attention" : "✅ Succès"}</AlertTitle>
    {message}
  </Alert>
);

// Custom Autocomplete that auto-creates new ingredients
const ProductAutocomplete = ({ options, value, onChange, label, size = "small", onAutoCreate, restaurantId }) => {
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  
  const handleChange = async (event, newValue) => {
    if (newValue && newValue.isNew) {
      // Auto-create new ingredient
      setLoading(true);
      try {
        const newStock = await StockService.findOrCreateStock(
          restaurantId,
          newValue.productName,
          newValue.unit || "piece",
          newValue.quantity || 0,
          newValue.pricePerUnit || 0
        );
        onChange(event, newStock);
        if (onAutoCreate) onAutoCreate(newStock);
      } catch (error) {
        console.error("Error auto-creating ingredient:", error);
      } finally {
        setLoading(false);
      }
    } else {
      onChange(event, newValue);
    }
  };
  
  const filterOptions = (options, state) => {
    const filtered = options.filter(opt => 
      opt.productName?.toLowerCase().includes(state.inputValue.toLowerCase())
    );
    
    // If typing something that doesn't exist, show option to create it
    if (state.inputValue && state.inputValue.trim() !== "") {
      const exists = options.some(opt => 
        opt.productName?.toLowerCase() === state.inputValue.toLowerCase()
      );
      
      if (!exists) {
        filtered.push({
          id: "new-item",
          productName: state.inputValue,
          isNew: true,
          unit: "piece",
          quantity: 0,
          pricePerUnit: 0
        });
      }
    }
    
    return filtered;
  };
  
  const getOptionLabel = (option) => {
    if (option.isNew) return `➕ Créer "${option.productName}" (Nouvel ingrédient)`;
    return `${option.productName} (${option.unit || '-'} - ${formatNumber(option.pricePerUnit)} TND)`;
  };
  
  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(e, v) => setInputValue(v)}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      loading={loading}
      renderInput={(params) => (
        <TextField 
          {...params} 
          label={label} 
          size={size}
          helperText="💡 Tapez un nom et sélectionnez 'Créer' pour ajouter un nouvel ingrédient"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} style={{ 
          ...props.style, 
          backgroundColor: option.isNew ? "#e0f2fe" : "transparent",
          fontWeight: option.isNew ? 600 : "normal"
        }}>
          {option.isNew ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AddCircle sx={{ fontSize: 18, color: "#10b981" }} />
              <Typography>✨ Créer "{option.productName}" (Nouvel ingrédient)</Typography>
              <Chip label="Auto-création" size="small" sx={{ ml: 1, bgcolor: "#10b981", color: "white", fontSize: "0.7rem" }} />
            </Box>
          ) : (
            <Box>
              <Typography variant="body2">{option.productName}</Typography>
              <Typography variant="caption" color="text.secondary">
                📦 Stock: {formatNumber(option.quantity)} {option.unit} | 💰 {formatNumber(option.pricePerUnit)} TND/{option.unit}
              </Typography>
            </Box>
          )}
        </li>
      )}
    />
  );
};

// ==================== MAIN COMPONENT ====================
const FactureScreen = () => {
  const theme = useTheme();
  const restaurantId = localStorage.getItem("restaurantId");
  
  // State
  const [loading, setLoading] = useState(true);
  const [stock, setStock] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [factures, setFactures] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tab, setTab] = useState(0);
  const [showHints, setShowHints] = useState(true);
  const [hintAnchor, setHintAnchor] = useState(null);
  
  // Dialogs
  const [openFacture, setOpenFacture] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [openSupplier, setOpenSupplier] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [exportMenu, setExportMenu] = useState(null);
  const [supplierEdit, setSupplierEdit] = useState(null);
  const [showSupplierWarning, setShowSupplierWarning] = useState(false);
  
  // Forms
  const [factureForm, setFactureForm] = useState({
    supplierId: "", numero: "", date: new Date(), items: [], notes: "",
    achatHTVA: 0, tvaTotale: 0, achatTTC: 0, montantPaye: 0, resteAPayer: 0,
    modeDePaiement: "cash", paymentnum: "", discount: 0,
    retenueSource: { enabled: false, taux: 0, montant: 0 }
  });
  
  const [supplierForm, setSupplierForm] = useState({
    name: "", email: "", numero: "", address: "", taxId: "", paymentTerms: "30_days", 
    retenueSource: { enabled: false, taux: 0 }, listDeProduit: []
  });
  
  const [paymentForm, setPaymentForm] = useState({
    montantPaye: 0, modeDePaiement: "cash", paymentnum: "", datePaiement: new Date(), notes: ""
  });
  
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // ==================== HELPERS ====================
  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });
  
  const formatDate = (d) => {
    if (!d) return '';
    try {
      const date = d?.toDate ? d.toDate() : new Date(d);
      return isNaN(date.getTime()) ? '' : date.toLocaleDateString('fr-FR');
    } catch { return ''; }
  };
  
  const generateNumero = () => {
    const d = new Date();
    return `FACT-${d.getFullYear().toString().substr(-2)}${(d.getMonth()+1).toString().padStart(2,'0')}${d.getDate().toString().padStart(2,'0')}-${Math.floor(Math.random()*1000).toString().padStart(3,'0')}`;
  };
  
  const getStatus = (s) => ({ impayée: "Impayée", partiellement_payée: "Partiellement payée", payée: "Payée" }[s] || s);
  const getStatusColor = (s) => ({ impayée: "#ef4444", partiellement_payée: "#f59e0b", payée: "#10b981" }[s] || "#6b7280");
  
  // ==================== CALCULATIONS ====================
  const calculateTotals = useCallback(() => {
    // Calculate subtotal (HT)
    const subtotal = factureForm.items.reduce((sum, i) => sum + (parseNumber(i.quantity) * parseNumber(i.unitPrice)), 0);
    
    // Apply discount
    const discountAmount = subtotal * (factureForm.discount / 100);
    const achatHTVA = subtotal - discountAmount;
    
    // Calculate TVA per item based on each item's TVA rate
    let tvaTotale = 0;
    factureForm.items.forEach(item => {
      const itemTotalHT = parseNumber(item.quantity) * parseNumber(item.unitPrice);
      const itemTVA = itemTotalHT * ((item.tvaRate || 19) / 100);
      tvaTotale += itemTVA;
    });
    
    let achatTTC = achatHTVA + tvaTotale;
    
    // Apply retenue à la source
    let retenueMontant = 0;
    if (factureForm.retenueSource.enabled && factureForm.retenueSource.taux > 0) {
      retenueMontant = achatTTC * (factureForm.retenueSource.taux / 100);
      achatTTC = achatTTC - retenueMontant;
    }
    
    const montantPaye = parseNumber(factureForm.montantPaye) || 0;
    const resteAPayer = achatTTC - montantPaye;
    
    setFactureForm(prev => ({
      ...prev,
      achatHTVA: achatHTVA.toFixed(3),
      tvaTotale: tvaTotale.toFixed(3),
      achatTTC: achatTTC.toFixed(3),
      resteAPayer: resteAPayer.toFixed(3),
      retenueSource: { ...prev.retenueSource, montant: retenueMontant.toFixed(3) }
    }));
  }, [factureForm.items, factureForm.discount, factureForm.retenueSource.enabled, factureForm.retenueSource.taux, factureForm.montantPaye]);
  
  useEffect(() => {
    calculateTotals();
  }, [calculateTotals]);
  
  // ==================== LOAD DATA ====================
  const loadData = async () => {
    try {
      setLoading(true);
      const [stocksData, suppliersData, facturesData] = await Promise.all([
        StockService.getAll(restaurantId),
        Fournisseur.getAllFournisseurs(restaurantId),
        FactureService.getAll(restaurantId)
      ]);
      setStock(stocksData);
      setSuppliers(suppliersData);
      setFactures(facturesData);
      setFiltered(facturesData);
    } catch (error) {
      console.error("Error loading data:", error);
      showSnackbar("Erreur chargement données", "error");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => { if (restaurantId) loadData(); }, [restaurantId]);
  
  useEffect(() => {
    let filteredData = [...factures];
    if (search) filteredData = filteredData.filter(f => 
      f.numero?.toLowerCase().includes(search.toLowerCase()) || 
      f.supplierName?.toLowerCase().includes(search.toLowerCase())
    );
    if (status !== "all") filteredData = filteredData.filter(f => f.statut === status);
    if (supplierFilter !== "all") filteredData = filteredData.filter(f => f.supplierId === supplierFilter);
    setFiltered(filteredData);
    setPage(0);
  }, [search, factures, status, supplierFilter]);
  
  // ==================== FACTURE CRUD ====================
  const addItem = () => {
    setFactureForm(prev => ({
      ...prev,
      items: [...prev.items, { 
        id: Date.now(), 
        productId: "", 
        productName: "", 
        quantity: 1, 
        unitPrice: 0, 
        total: 0, 
        unit: "piece",
        tvaRate: 19 // Default TVA rate (19%)
      }]
    }));
  };
  
  const updateItem = (index, field, value) => {
    const newItems = [...factureForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    if (field === 'productId' && value) {
      const product = stock.find(p => p.id === value);
      if (product) {
        newItems[index].productName = product.productName;
        newItems[index].unitPrice = product.pricePerUnit || 0;
        newItems[index].unit = product.unit || "piece";
      }
    }
    
    if (field === 'quantity' || field === 'unitPrice') {
      newItems[index].total = parseNumber(newItems[index].quantity) * parseNumber(newItems[index].unitPrice);
    }
    
    setFactureForm(prev => ({ ...prev, items: newItems }));
  };
  
  const removeItem = (index) => {
    const newItems = [...factureForm.items];
    newItems.splice(index, 1);
    setFactureForm(prev => ({ ...prev, items: newItems }));
  };
  
  // Handle auto-created ingredient
  const handleAutoCreateIngredient = (index, newStock) => {
    // Update the item with the newly created stock
    const newItems = [...factureForm.items];
    newItems[index] = {
      ...newItems[index],
      productId: newStock.id,
      productName: newStock.productName,
      unitPrice: newStock.pricePerUnit,
      unit: newStock.unit,
      total: parseNumber(newItems[index].quantity) * parseNumber(newStock.pricePerUnit)
    };
    setFactureForm(prev => ({ ...prev, items: newItems }));
    
    // Add to stock list
    setStock(prev => [...prev, newStock]);
    
    showSnackbar(`✨ Nouvel ingrédient "${newStock.productName}" créé automatiquement avec succès!`, "success");
  };
  
  // Save facture with stock update and product cost recalculation
  const saveFacture = async () => {
    try {
      if (!factureForm.supplierId) {
        setShowSupplierWarning(true);
        showSnackbar("⚠️ Veuillez sélectionner un fournisseur. Cliquez sur 'Nouveau fournisseur' si besoin.", "warning");
        return;
      }
      
      if (factureForm.items.length === 0) {
        showSnackbar("⚠️ Veuillez ajouter au moins un article à la facture", "warning");
        return;
      }
  
      const invalidItems = factureForm.items.filter(item => !item.productId);
      if (invalidItems.length > 0) {
        showSnackbar("⚠️ Veuillez sélectionner un produit pour chaque article. Vous pouvez créer un nouvel ingrédient en tapant son nom.", "warning");
        return;
      }
  
      const supplier = suppliers.find(s => s.id === factureForm.supplierId);
      if (!supplier) {
        showSnackbar("Fournisseur non trouvé", "error");
        return;
      }
  
      // Calculate totals with per-item TVA
      const subtotal = factureForm.items.reduce((sum, i) => sum + (parseNumber(i.quantity) * parseNumber(i.unitPrice)), 0);
      const discountAmount = subtotal * (factureForm.discount / 100);
      const achatHTVA = subtotal - discountAmount;
      
      let tvaTotale = 0;
      factureForm.items.forEach(item => {
        const itemTotalHT = parseNumber(item.quantity) * parseNumber(item.unitPrice);
        const itemTVA = itemTotalHT * ((item.tvaRate || 19) / 100);
        tvaTotale += itemTVA;
      });
      
      let achatTTC = achatHTVA + tvaTotale;
      
      let retenueMontant = 0;
      if (factureForm.retenueSource.enabled && factureForm.retenueSource.taux > 0) {
        retenueMontant = achatTTC * (factureForm.retenueSource.taux / 100);
        achatTTC = achatTTC - retenueMontant;
      }
      
      const montantPaye = parseNumber(factureForm.montantPaye) || 0;
      const resteAPayer = achatTTC - montantPaye;
      const statut = montantPaye >= achatTTC ? "payée" : montantPaye > 0 ? "partiellement_payée" : "impayée";
  
      // Track updated products for summary
      const stockUpdateResults = [];
      
      // First, update stock for each item (this will also update product costs)
      for (const item of factureForm.items) {
        const result = await StockService.updateStockWithPurchase(
          restaurantId,
          item.productName,
          item.unit || "piece",
          parseNumber(item.quantity),
          parseNumber(item.unitPrice)
        );
        stockUpdateResults.push({ item: item.productName, ...result });
        
        if (!result.success) {
          showSnackbar(`Erreur stock pour ${item.productName}: ${result.message}`, "error");
          return;
        }
      }
      
      // Get all products to show which ones were updated
      const allProducts = await StockService.getAllProducts(restaurantId);
      
      // Prepare facture data with stock update info and product cost updates
      const factureData = {
        fournisseurId: factureForm.supplierId,
        supplierName: supplier.name,
        numero: factureForm.numero || generateNumero(),
        date: Timestamp.fromDate(new Date(factureForm.date)),
        items: factureForm.items.map(item => ({
          id: item.id,
          productId: item.productId,
          productName: item.productName,
          quantity: parseNumber(item.quantity),
          unitPrice: parseNumber(item.unitPrice).toFixed(3),
          unit: item.unit || "piece",
          tvaRate: item.tvaRate || 19,
          totalHT: (parseNumber(item.quantity) * parseNumber(item.unitPrice)).toFixed(3),
          tvaAmount: ((parseNumber(item.quantity) * parseNumber(item.unitPrice)) * ((item.tvaRate || 19) / 100)).toFixed(3),
          totalTTC: ((parseNumber(item.quantity) * parseNumber(item.unitPrice)) * (1 + ((item.tvaRate || 19) / 100))).toFixed(3)
        })),
        notes: factureForm.notes || "",
        achatHTVA: achatHTVA.toFixed(3),
        tvaTotale: tvaTotale.toFixed(3),
        achatTTC: achatTTC.toFixed(3),
        montantPaye: montantPaye.toFixed(3),
        resteAPayer: resteAPayer.toFixed(3),
        statut: statut,
        modeDePaiement: factureForm.modeDePaiement,
        paymentnum: factureForm.paymentnum || "",
        discount: factureForm.discount,
        retenueSource: {
          enabled: factureForm.retenueSource.enabled,
          taux: factureForm.retenueSource.taux || 0,
          montant: retenueMontant.toFixed(3)
        },
        stockUpdates: stockUpdateResults.map(r => ({
          product: r.item,
          oldQuantity: r.oldQty,
          newQuantity: r.newQuantity,
          oldPrice: r.oldPrice,
          newPrice: r.newPricePerUnit
        })),
        createdBy: localStorage.getItem("userName") || "Utilisateur",
        paymentHistory: montantPaye > 0 ? [{
          date: Timestamp.now(),
          montant: montantPaye.toFixed(3),
          mode: factureForm.modeDePaiement,
          numero: factureForm.paymentnum || "",
          notes: "Paiement initial"
        }] : []
      };
  
      console.log("Saving facture:", factureData);
      const saved = await FactureService.create(restaurantId, factureData);
      
      if (saved && saved.id) {
        // Build summary message
        let stockSummary = stockUpdateResults.map(r => 
          `${r.item}: ${r.oldQty?.toFixed(2) || 0} → ${r.newQuantity.toFixed(2)} (prix: ${parseFloat(r.newPricePerUnit).toFixed(3)} TND)`
        ).join("\n");
        
        // Check if any product costs were affected
        const updatedProducts = allProducts.filter(p => 
          p.ingredients?.some(ing => 
            factureForm.items.some(item => item.productName === ing.stockName)
          )
        );
        
        if (updatedProducts.length > 0) {
          stockSummary += `\n\n✅ Prix de revient mis à jour pour ${updatedProducts.length} produit(s)`;
        }
        
        showSnackbar(`✅ Facture créée avec succès!\n\n📦 Mise à jour stock:\n${stockSummary}`, "success");
        
        // Refresh stock list
        const updatedStock = await StockService.getAll(restaurantId);
        setStock(updatedStock);
        
        // Refresh factures list
        const updatedFactures = await FactureService.getAll(restaurantId);
        setFactures(updatedFactures);
        
        setOpenFacture(false);
        
        // Reset form
        setFactureForm({
          supplierId: "", numero: generateNumero(), date: new Date(), items: [], notes: "",
          achatHTVA: 0, tvaTotale: 0, achatTTC: 0, montantPaye: 0, resteAPayer: 0,
          modeDePaiement: "cash", paymentnum: "", discount: 0,
          retenueSource: { enabled: false, taux: 0, montant: 0 }
        });
      }
      
    } catch (error) {
      console.error("Error saving facture:", error);
      showSnackbar(`❌ Erreur: ${error.message}`, "error");
    }
  };
  
  const recordPayment = async () => {
    try {
      if (!selected) return;
      const paymentAmount = parseNumber(paymentForm.montantPaye);
      if (paymentAmount <= 0) {
        showSnackbar("⚠️ Montant invalide", "warning");
        return;
      }
      if (paymentAmount > parseNumber(selected.resteAPayer)) {
        showSnackbar("⚠️ Montant dépasse le reste à payer", "warning");
        return;
      }
  
      const newMontantPaye = parseNumber(selected.montantPaye) + paymentAmount;
      const newResteAPayer = parseNumber(selected.achatTTC) - newMontantPaye;
      const newStatut = newMontantPaye >= parseNumber(selected.achatTTC) ? "payée" : newMontantPaye > 0 ? "partiellement_payée" : "impayée";
  
      const updatedFacture = {
        ...selected,
        montantPaye: newMontantPaye.toFixed(3),
        resteAPayer: newResteAPayer.toFixed(3),
        statut: newStatut,
        modeDePaiement: paymentForm.modeDePaiement,
        paymentnum: paymentForm.paymentnum,
        paymentHistory: [
          ...(selected.paymentHistory || []),
          {
            date: Timestamp.fromDate(paymentForm.datePaiement),
            montant: paymentAmount.toFixed(3),
            mode: paymentForm.modeDePaiement,
            numero: paymentForm.paymentnum,
            notes: paymentForm.notes
          }
        ]
      };
  
      const updateData = { ...updatedFacture };
      delete updateData.id;
      delete updateData.createdAt;
      delete updateData.updatedAt;
  
      await FactureService.update(restaurantId, selected.id, updateData);
      const newFactures = await FactureService.getAll(restaurantId);
      setFactures(newFactures);
      
      setOpenPayment(false);
      setSelected(null);
      showSnackbar("✅ Paiement enregistré avec succès", "success");
    } catch (error) {
      console.error("Error recording payment:", error);
      showSnackbar("❌ Erreur paiement", "error");
    }
  };
  
  // ==================== SUPPLIER CRUD ====================
  const saveSupplier = async () => {
    try {
      if (!supplierForm.name) {
        showSnackbar("⚠️ Le nom du fournisseur est requis", "warning");
        return;
      }
      
      const supplierData = { ...supplierForm, restaurantId, updatedAt: new Date().toISOString() };
      
      if (supplierEdit) {
        await Fournisseur.updateFournisseur(restaurantId, supplierEdit.id, supplierData);
        showSnackbar("✅ Fournisseur modifié avec succès", "success");
      } else {
        supplierData.createdAt = new Date().toISOString();
        await Fournisseur.addFournisseur(restaurantId, supplierData);
        showSnackbar("✅ Fournisseur ajouté avec succès", "success");
        // Auto-select the new supplier
        const updatedSuppliers = await Fournisseur.getAllFournisseurs(restaurantId);
        const newSupplier = updatedSuppliers.find(s => s.name === supplierForm.name);
        if (newSupplier) {
          setFactureForm(prev => ({ ...prev, supplierId: newSupplier.id }));
          setShowSupplierWarning(false);
        }
      }
      
      const updatedSuppliers = await Fournisseur.getAllFournisseurs(restaurantId);
      setSuppliers(updatedSuppliers);
      setOpenSupplier(false);
      setSupplierEdit(null);
      setSupplierForm({
        name: "", email: "", numero: "", address: "", taxId: "", paymentTerms: "30_days", 
        retenueSource: { enabled: false, taux: 0 }, listDeProduit: []
      });
    } catch (error) {
      console.error("Error saving supplier:", error);
      showSnackbar("❌ Erreur lors de l'enregistrement", "error");
    }
  };
  
  // ==================== STATS ====================
  const stats = {
    total: factures.reduce((s, f) => s + parseNumber(f.achatTTC), 0),
    paid: factures.reduce((s, f) => s + parseNumber(f.montantPaye), 0),
    remaining: factures.reduce((s, f) => s + parseNumber(f.resteAPayer), 0),
    unpaid: factures.filter(f => f.statut === 'impayée').length,
    partial: factures.filter(f => f.statut === 'partiellement_payée').length
  };
  
  // ==================== EXPORT ====================
  const exportToExcel = (data, name) => {
    try {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' }), `${name}_${new Date().toISOString().split('T')[0]}.xlsx`);
      showSnackbar("✅ Export réussi", "success");
    } catch (error) {
      showSnackbar("❌ Erreur export", "error");
    }
  };
  
  // ==================== RENDER ====================
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress size={60} />
      </Box>
    );
  }
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
      <Box sx={{ minHeight: "100vh", bgcolor: "#f3f4f6" }}>
        {/* Header */}
        <Paper sx={{ borderRadius: 0, bgcolor: "white", borderBottom: 1, borderColor: "divider", position: "sticky", top: 0, zIndex: 10 }}>
          <Box sx={{ p: 2, maxWidth: 1400, mx: "auto" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Avatar sx={{ bgcolor: "#2563eb" }}><Receipt /></Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>Gestion des Factures Fournisseurs</Typography>
                <Typography variant="body2" color="text.secondary">
                  {suppliers.length} fournisseurs · {factures.length} factures · 
                  {formatNumber(stock.reduce((sum, s) => sum + (s.quantity || 0), 0))} unités en stock
                </Typography>
              </Box>
              <Tooltip title="Afficher les astuces">
                <IconButton onClick={(e) => setHintAnchor(e.currentTarget)}>
                  <HelpOutline />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={() => setOpenFacture(true)}>Nouvelle facture</Button>
              <IconButton onClick={(e) => setExportMenu(e.currentTarget)}><MoreVert /></IconButton>
              <MuiMenu anchorEl={exportMenu} open={Boolean(exportMenu)} onClose={() => setExportMenu(null)}>
                <MuiMenuItem onClick={() => { 
                  exportToExcel(filtered.map(f => ({ 
                    'N°': f.numero, 'Fournisseur': f.supplierName, 'Date': formatDate(f.date), 
                    'Montant HT': formatNumber(f.achatHTVA), 'TVA Totale': formatNumber(f.tvaTotale), 
                    'Montant TTC': formatNumber(f.achatTTC), 
                    'Payé': formatNumber(f.montantPaye), 'Reste': formatNumber(f.resteAPayer), 
                    'Retenue Source': f.retenueSource?.enabled ? `${f.retenueSource.taux}%` : 'Non',
                    'Statut': getStatus(f.statut) 
                  })), 'factures'); 
                  setExportMenu(null); 
                }}>
                  <ListItemIcon><Download fontSize="small" /></ListItemIcon><ListItemText>Exporter factures</ListItemText>
                </MuiMenuItem>
                <MuiMenuItem onClick={() => { 
                  exportToExcel(suppliers.map(s => ({ 
                    'Nom': s.name, 'Email': s.email, 'Téléphone': s.numero, 'Adresse': s.address, 
                    'N° TVA': s.taxId, 'Retenue Source': s.retenueSource?.enabled ? `${s.retenueSource?.taux}%` : 'Non' 
                  })), 'fournisseurs'); 
                  setExportMenu(null); 
                }}>
                  <ListItemIcon><Download fontSize="small" /></ListItemIcon><ListItemText>Exporter fournisseurs</ListItemText>
                </MuiMenuItem>
              </MuiMenu>
            </Box>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mt: 2 }}>
              <Tab label="📄 Factures" /><Tab label="🏢 Fournisseurs" />
            </Tabs>
          </Box>
        </Paper>
        
        <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
        
          {/* Stats Cards */}
          {tab === 0 && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard 
                  title="Total Achats" 
                  value={`${formatNumber(stats.total)} TND`} 
                  icon={Receipt} 
                  color="#3b82f6"
                  tooltip="Montant total de toutes les factures (TTC)"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard 
                  title="Total Payé" 
                  value={`${formatNumber(stats.paid)} TND`} 
                  icon={Payment} 
                  color="#10b981"
                  tooltip="Montant total déjà payé aux fournisseurs"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard 
                  title="Reste à Payer" 
                  value={`${formatNumber(stats.remaining)} TND`} 
                  icon={MoneyIcon} 
                  color="#f59e0b"
                  tooltip="Montant total restant à payer"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard 
                  title="Factures Impayées" 
                  value={stats.unpaid + stats.partial} 
                  icon={WarningIcon} 
                  color="#ef4444"
                  subtitle={`${stats.unpaid} impayées, ${stats.partial} partielles`}
                  tooltip="Nombre de factures non entièrement payées"
                />
              </Grid>
            </Grid>
          )}
          
          {/* Filters */}
          {tab === 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: "2px 4px", display: "flex", alignItems: "center", border: 1, borderColor: "divider" }}>
                    <Search /><InputBase sx={{ ml: 1, flex: 1 }} placeholder="🔍 Rechercher facture ou fournisseur..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    {search && <IconButton onClick={() => setSearch("")}><Close /></IconButton>}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Statut</InputLabel>
                    <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Statut">
                      <MuiMenuItem value="all">📊 Tous</MuiMenuItem>
                      <MuiMenuItem value="impayée">⚠️ Impayée</MuiMenuItem>
                      <MuiMenuItem value="partiellement_payée">🔄 Partiellement payée</MuiMenuItem>
                      <MuiMenuItem value="payée">✅ Payée</MuiMenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Fournisseur</InputLabel>
                    <Select value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)} label="Fournisseur">
                      <MuiMenuItem value="all">🏢 Tous</MuiMenuItem>
                      {suppliers.map(s => <MuiMenuItem key={s.id} value={s.id}>{s.name}</MuiMenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button fullWidth variant="outlined" onClick={() => { setSearch(""); setStatus("all"); setSupplierFilter("all"); }}>
                    🔄 Réinitialiser
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}
          
          {/* Factures Table */}
          {tab === 0 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell>📄 N° Facture</TableCell><TableCell>📅 Date</TableCell><TableCell>🏢 Fournisseur</TableCell>
                    <TableCell align="right">💰 Montant HT</TableCell>
                    <TableCell align="right">📊 TVA</TableCell>
                    <TableCell align="right">💵 Montant TTC</TableCell>
                    <TableCell align="right">✅ Payé</TableCell>
                    <TableCell align="right">⚠️ Reste</TableCell>
                    <TableCell>🔖 Retenue</TableCell><TableCell>📌 Statut</TableCell><TableCell align="center">⚙️ Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(f => (
                    <TableRow key={f.id} hover>
                      <TableCell><Typography fontWeight={500}>{f.numero}</Typography></TableCell>
                      <TableCell>{formatDate(f.date)}</TableCell>
                      <TableCell><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Avatar sx={{ width: 24, height: 24, bgcolor: "rgba(37,99,235,0.1)" }}><Person sx={{ fontSize: 16 }} /></Avatar>{f.supplierName}</Box></TableCell>
                      <TableCell align="right">{formatNumber(f.achatHTVA)} TND</TableCell>
                      <TableCell align="right">{formatNumber(f.tvaTotale)} TND</TableCell>
                      <TableCell align="right">{formatNumber(f.achatTTC)} TND</TableCell>
                      <TableCell align="right" sx={{ color: "#10b981" }}>{formatNumber(f.montantPaye)} TND</TableCell>
                      <TableCell align="right" sx={{ color: parseNumber(f.resteAPayer) > 0 ? "#ef4444" : "#10b981" }}>{formatNumber(f.resteAPayer)} TND</TableCell>
                      <TableCell>{f.retenueSource?.enabled ? `${f.retenueSource.taux}%` : '-'}</TableCell>
                      <TableCell><Chip label={getStatus(f.statut)} size="small" sx={{ bgcolor: `rgba(${getStatusColor(f.statut) === "#10b981" ? "16,185,129" : getStatusColor(f.statut) === "#f59e0b" ? "245,158,11" : "239,68,68"}, 0.1)`, color: getStatusColor(f.statut) }} /></TableCell>
                      <TableCell align="center">
                        <Tooltip title="Voir détails">
                          <IconButton size="small" onClick={() => { setSelected(f); setOpenDetails(true); }}><Visibility /></IconButton>
                        </Tooltip>
                        {f.statut !== 'payée' && (
                          <Tooltip title="Enregistrer un paiement">
                            <IconButton size="small" color="success" onClick={() => { setSelected(f); setOpenPayment(true); }}><Payment /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!filtered.length && (
                    <TableRow><TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                      <Receipt sx={{ fontSize: 50, color: "#ccc" }} />
                      <Typography>Aucune facture trouvée</Typography>
                      <Button variant="contained" startIcon={<Add />} onClick={() => setOpenFacture(true)} sx={{ mt: 2 }}>Créer ma première facture</Button>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination rowsPerPageOptions={[10,25,50]} component="div" count={filtered.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }} labelRowsPerPage="Lignes par page" />
            </TableContainer>
          )}
          
          {/* Suppliers Table */}
          {tab === 1 && (
            <Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead sx={{ bgcolor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell>🏢 Fournisseur</TableCell>
                      <TableCell>📞 Contact</TableCell>
                      <TableCell>⏱️ Conditions</TableCell>
                      <TableCell>🔖 Retenue Source</TableCell>
                      <TableCell>📄 Factures</TableCell>
                      <TableCell align="center">⚙️ Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suppliers.map(s => (
                      <TableRow key={s.id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ bgcolor: "rgba(37,99,235,0.1)" }}><Business /></Avatar>
                            <Box>
                              <Typography fontWeight={500}>{s.name}</Typography>
                              {s.address && <Typography variant="caption" color="text.secondary">{s.address}</Typography>}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{s.email || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.numero || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={{ immediate: "Immédiat", "7_days": "7j", "15_days": "15j", "30_days": "30j", "45_days": "45j", "60_days": "60j" }[s.paymentTerms] || "30j"} size="small" />
                        </TableCell>
                        <TableCell>{s.retenueSource?.enabled ? `${s.retenueSource.taux}%` : "Non"}</TableCell>
                        <TableCell>
                          <Badge badgeContent={factures.filter(f => f.supplierId === s.id).length} color="primary">
                            <Receipt />
                          </Badge>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Modifier">
                            <IconButton size="small" onClick={() => { setSupplierEdit(s); setSupplierForm(s); setOpenSupplier(true); }}><Edit /></IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton size="small" color="error" onClick={() => { 
                              setConfirmAction({ title: "Supprimer le fournisseur", message: `Êtes-vous sûr de vouloir supprimer "${s.name}" ? Cette action est irréversible.`, onConfirm: async () => { 
                                await Fournisseur.deleteFournisseur(restaurantId, s.id); 
                                setSuppliers(await Fournisseur.getAllFournisseurs(restaurantId)); 
                                setOpenConfirm(false); 
                                showSnackbar(`✅ Fournisseur "${s.name}" supprimé`, "success"); 
                              } }); 
                              setOpenConfirm(true); 
                            }}><Delete /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {suppliers.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <Business sx={{ fontSize: 50, color: "#ccc" }} />
                        <Typography>Aucun fournisseur</Typography>
                        <Button variant="contained" startIcon={<Add />} onClick={() => setOpenSupplier(true)} sx={{ mt: 2 }}>Ajouter un fournisseur</Button>
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
        
        {/* New Facture Dialog */}
        <StyledDialog open={openFacture} onClose={() => setOpenFacture(false)} title="Nouvelle facture fournisseur" icon={Receipt} maxWidth="lg" 
          actions={
            <Box sx={{ display: "flex", gap: 2, justifyContent: "space-between", width: "100%" }}>
              <Button variant="outlined" startIcon={<Business />} onClick={() => setOpenSupplier(true)}>
                ➕ Nouveau fournisseur
              </Button>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button onClick={() => setOpenFacture(false)}>Annuler</Button>
                <Button variant="contained" onClick={saveFacture} startIcon={<Save />} sx={{ bgcolor: "#10b981" }}>
                  💾 Enregistrer la facture
                </Button>
              </Box>
            </Box>
          }>
          {showSupplierWarning && suppliers.length === 0 && (
            <InfoAlert 
              message="Aucun fournisseur n'est enregistré. Cliquez sur 'Nouveau fournisseur' pour en ajouter un avant de créer la facture."
              severity="warning"
              onClose={() => setShowSupplierWarning(false)}
            />
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InfoAlert 
                message="💡 Astuce: Pour ajouter un nouvel ingrédient, tapez son nom dans le champ produit et sélectionnez l'option 'Créer'. Il sera automatiquement ajouté à votre stock avec la quantité et le prix saisis."
                severity="info"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>🏢 Fournisseur *</InputLabel>
                <Select 
                  value={factureForm.supplierId} 
                  onChange={(e) => setFactureForm({ ...factureForm, supplierId: e.target.value })}
                  label="🏢 Fournisseur *"
                >
                  {suppliers.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                label="📄 N° Facture" 
                value={factureForm.numero} 
                onChange={(e) => setFactureForm({ ...factureForm, numero: e.target.value })}
                helperText="Laissez vide pour génération automatique"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker 
                label="📅 Date de facture" 
                value={factureForm.date} 
                onChange={(d) => setFactureForm({ ...factureForm, date: d })} 
                renderInput={(p) => <TextField {...p} fullWidth />} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>💳 Mode de paiement</InputLabel>
                <Select value={factureForm.modeDePaiement} onChange={(e) => setFactureForm({ ...factureForm, modeDePaiement: e.target.value })} label="💳 Mode de paiement">
                  <MenuItem value="cash">💵 Espèces</MenuItem>
                  <MenuItem value="check">📝 Chèque</MenuItem>
                  <MenuItem value="transfer">🏦 Virement bancaire</MenuItem>
                  <MenuItem value="card">💳 Carte bancaire</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="🔢 N° paiement / Référence" value={factureForm.paymentnum} onChange={(e) => setFactureForm({ ...factureForm, paymentnum: e.target.value })} />
            </Grid>
            
            {/* Retenue à la source */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: "#fef3c7", border: "1px solid #f59e0b" }}>
                <FormControlLabel 
                  control={<Checkbox checked={factureForm.retenueSource.enabled} onChange={(e) => setFactureForm({ ...factureForm, retenueSource: { ...factureForm.retenueSource, enabled: e.target.checked } })} />} 
                  label="🔖 Appliquer la retenue à la source" 
                />
                {factureForm.retenueSource.enabled && (
                  <TextField 
                    type="number" 
                    label="Taux (%)" 
                    value={factureForm.retenueSource.taux} 
                    onChange={(e) => setFactureForm({ ...factureForm, retenueSource: { ...factureForm.retenueSource, taux: parseFloat(e.target.value) || 0 } })} 
                    sx={{ mt: 1, width: 200 }} 
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} 
                    helperText="Pourcentage à déduire à la source"
                  />
                )}
              </Paper>
            </Grid>
            
            {/* Items with custom Autocomplete that auto-creates new ingredients */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                📦 Articles de la facture
                <Tooltip title="Ajoutez les produits achetés. Tapez un nouveau nom pour créer automatiquement l'ingrédient">
                  <HelpOutline sx={{ ml: 1, fontSize: 18, color: "#94a3b8", cursor: "help" }} />
                </Tooltip>
              </Typography>
              {factureForm.items.map((item, idx) => (
                <Paper key={item.id} sx={{ p: 2, mb: 2, bgcolor: "#f8fafc", position: "relative" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <ProductAutocomplete 
                        options={stock}
                        value={stock.find(s => s.id === item.productId) || null}
                        onChange={(e, v) => {
                          if (v && v.id && v.id !== "new-item") {
                            updateItem(idx, 'productId', v.id);
                          } else if (!v) {
                            updateItem(idx, 'productId', null);
                          }
                        }}
                        onAutoCreate={(newStock) => handleAutoCreateIngredient(idx, newStock)}
                        restaurantId={restaurantId}
                        label="🛒 Produit / Ingrédient"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField 
                        fullWidth 
                        label="📊 Quantité" 
                        type="number" 
                        size="small" 
                        value={item.quantity} 
                        onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                        helperText={`Unité: ${item.unit || 'piece'}`}
                        inputProps={{ step: "0.001" }}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField 
                        fullWidth 
                        label="💰 Prix unitaire" 
                        type="number" 
                        size="small" 
                        value={item.unitPrice} 
                        onChange={(e) => updateItem(idx, 'unitPrice', e.target.value)} 
                        InputProps={{ endAdornment: <InputAdornment position="end">TND</InputAdornment> }}
                        inputProps={{ step: "0.001" }}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>📊 TVA</InputLabel>
                        <Select 
                          value={item.tvaRate || 19} 
                          onChange={(e) => updateItem(idx, 'tvaRate', e.target.value)}
                          label="📊 TVA"
                        >
                          <MenuItem value={0}>0% (Exonéré)</MenuItem>
                          <MenuItem value={7}>7% (Taux réduit)</MenuItem>
                          <MenuItem value={19}>19% (Taux normal)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField 
                        fullWidth 
                        label="💵 Total HT" 
                        value={formatNumber((parseNumber(item.quantity) * parseNumber(item.unitPrice)))} 
                        size="small" 
                        InputProps={{ readOnly: true }} 
                      />
                    </Grid>
                    <Grid item xs={6} md={1}>
                      <Tooltip title="Supprimer l'article">
                        <IconButton color="error" onClick={() => removeItem(idx)}><Delete /></IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                  {/* Show TVA amount for this item */}
                  {(item.quantity && item.unitPrice) && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      📊 TVA: {formatNumber((parseNumber(item.quantity) * parseNumber(item.unitPrice)) * ((item.tvaRate || 19) / 100))} TND | 
                      💵 Total TTC: {formatNumber((parseNumber(item.quantity) * parseNumber(item.unitPrice)) * (1 + ((item.tvaRate || 19) / 100)))} TND
                    </Typography>
                  )}
                  {item.productId && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      📦 Unité: {item.unit}
                    </Typography>
                  )}
                </Paper>
              ))}
              <Button startIcon={<Add />} onClick={addItem} variant="outlined">
                ➕ Ajouter un article
              </Button>
            </Grid>
            
            {/* Totals */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: "#f0f9ff" }}>
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid item xs={6} md={3}>
                    <TextField 
                      fullWidth 
                      label="🏷️ Remise (%)" 
                      type="number" 
                      value={factureForm.discount} 
                      onChange={(e) => setFactureForm({ ...factureForm, discount: parseFloat(e.target.value) || 0 })} 
                      size="small"
                      helperText="Remise globale sur la facture"
                      inputProps={{ step: "0.1" }}
                    />
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Box sx={{ width: 350 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography>💰 Total HT:</Typography>
                      <Typography fontWeight={500}>{formatNumber(factureForm.achatHTVA)} TND</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography>📊 TVA Totale:</Typography>
                      <Typography fontWeight={500}>+ {formatNumber(factureForm.tvaTotale)} TND</Typography>
                    </Box>
                    {factureForm.retenueSource.enabled && factureForm.retenueSource.taux > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography>🔖 Retenue source ({factureForm.retenueSource.taux}%):</Typography>
                        <Typography sx={{ color: "#ef4444" }}>- {formatNumber(factureForm.retenueSource.montant)} TND</Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="h6">💵 Total TTC:</Typography>
                      <Typography variant="h6" sx={{ color: "#2563eb", fontWeight: 700 }}>{formatNumber(factureForm.achatTTC)} TND</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                      <Typography>✅ Montant payé:</Typography>
                      <TextField 
                        type="number" 
                        size="small" 
                        value={factureForm.montantPaye} 
                        onChange={(e) => setFactureForm({ ...factureForm, montantPaye: e.target.value })} 
                        sx={{ width: 120 }} 
                        InputProps={{ endAdornment: <InputAdornment position="end">TND</InputAdornment> }}
                        inputProps={{ step: "0.001" }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                      <Typography variant="subtitle1">⚠️ Reste à payer:</Typography>
                      <Typography variant="subtitle1" sx={{ color: parseNumber(factureForm.resteAPayer) > 0 ? "#ef4444" : "#10b981", fontWeight: 700 }}>
                        {formatNumber(factureForm.resteAPayer)} TND
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                multiline 
                rows={2} 
                label="📝 Notes (optionnel)" 
                value={factureForm.notes} 
                onChange={(e) => setFactureForm({ ...factureForm, notes: e.target.value })}
                placeholder="Informations supplémentaires, conditions particulières..."
              />
            </Grid>
          </Grid>
        </StyledDialog>
        
        {/* Facture Details Dialog */}
        <StyledDialog open={openDetails} onClose={() => setOpenDetails(false)} title={`📄 Facture: ${selected?.numero}`} icon={Receipt} maxWidth="lg" 
          actions={
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", width: "100%" }}>
              <Button onClick={() => setOpenDetails(false)}>Fermer</Button>
              {selected?.statut !== 'payée' && (
                <Button variant="contained" color="success" startIcon={<Payment />} onClick={() => { setOpenDetails(false); setOpenPayment(true); }}>
                  Enregistrer un paiement
                </Button>
              )}
            </Box>
          }>
          {selected && <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography color="text.secondary">🏢 Fournisseur</Typography>
                <Typography fontWeight={500}>{selected.supplierName}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography color="text.secondary">📅 Date</Typography>
                <Typography>{formatDate(selected.date)}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography color="text.secondary">📌 Statut</Typography>
                <Chip 
                  label={getStatus(selected.statut)} 
                  size="small" 
                  sx={{ bgcolor: `rgba(${getStatusColor(selected.statut) === "#10b981" ? "16,185,129" : getStatusColor(selected.statut) === "#f59e0b" ? "245,158,11" : "239,68,68"}, 0.1)`, color: getStatusColor(selected.statut) }} 
                />
              </Grid>
            </Grid>
            
            {/* Stock Update Summary */}
            {selected.stockUpdates && selected.stockUpdates.length > 0 && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: "#e0f2fe" }}>
                <Typography fontWeight={600} sx={{ mb: 1 }}>📦 Mise à jour du stock (Prix moyen pondéré)</Typography>
                {selected.stockUpdates.map((update, idx) => (
                  <Typography key={idx} variant="body2">
                    • {update.product}: Qté {update.oldQuantity?.toFixed(2) || 0} → {update.newQuantity.toFixed(2)} | 
                    Prix {update.oldPrice?.toFixed(3) || 0} → {parseFloat(update.newPrice).toFixed(3)} TND
                  </Typography>
                ))}
              </Paper>
            )}
            
            <Typography fontWeight={600} sx={{ mb: 2 }}>📋 Détail des articles</Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell>🛒 Produit</TableCell>
                    <TableCell align="right">📊 Qté</TableCell>
                    <TableCell align="right">📦 Unité</TableCell>
                    <TableCell align="right">💰 Prix unitaire</TableCell>
                    <TableCell align="right">📊 TVA</TableCell>
                    <TableCell align="right">💵 Total HT</TableCell>
                    <TableCell align="right">💵 Total TTC</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selected.items?.map(i => (
                    <TableRow key={i.id}>
                      <TableCell>{i.productName}</TableCell>
                      <TableCell align="right">{i.quantity}</TableCell>
                      <TableCell align="right">{i.unit || '-'}</TableCell>
                      <TableCell align="right">{formatNumber(i.unitPrice)} TND</TableCell>
                      <TableCell align="right">{i.tvaRate || 19}%</TableCell>
                      <TableCell align="right">{formatNumber(i.totalHT)} TND</TableCell>
                      <TableCell align="right">{formatNumber(i.totalTTC)} TND</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Paper sx={{ p: 2, mb: 3, bgcolor: "#f8fafc" }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Box sx={{ width: 300 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography>💰 Total HT:</Typography>
                    <Typography>{formatNumber(selected.achatHTVA)} TND</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography>📊 TVA Totale:</Typography>
                    <Typography>+ {formatNumber(selected.tvaTotale)} TND</Typography>
                  </Box>
                  {selected.retenueSource?.enabled && selected.retenueSource?.taux > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography>🔖 Retenue source ({selected.retenueSource.taux}%):</Typography>
                      <Typography sx={{ color: "#ef4444" }}>- {formatNumber(selected.retenueSource.montant)} TND</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h6">💵 Total TTC:</Typography>
                    <Typography variant="h6">{formatNumber(selected.achatTTC)} TND</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography>✅ Payé:</Typography>
                    <Typography sx={{ color: "#10b981" }}>{formatNumber(selected.montantPaye)} TND</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography>⚠️ Reste:</Typography>
                    <Typography sx={{ color: parseNumber(selected.resteAPayer) > 0 ? "#ef4444" : "#10b981" }}>{formatNumber(selected.resteAPayer)} TND</Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
            {selected.paymentHistory?.length > 0 && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography fontWeight={600} sx={{ mb: 2 }}>📜 Historique des paiements</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>📅 Date</TableCell>
                      <TableCell>💰 Montant</TableCell>
                      <TableCell>💳 Mode</TableCell>
                      <TableCell>🔢 Référence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selected.paymentHistory.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>{formatDate(p.date)}</TableCell>
                        <TableCell>{formatNumber(p.montant)} TND</TableCell>
                        <TableCell>{p.mode}</TableCell>
                        <TableCell>{p.numero || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Box>}
        </StyledDialog>
        
        {/* Payment Dialog */}
        <StyledDialog open={openPayment} onClose={() => setOpenPayment(false)} title={`💳 Paiement - ${selected?.numero}`} icon={Payment} maxWidth="sm" 
          actions={<><Button onClick={() => setOpenPayment(false)}>Annuler</Button><Button variant="contained" color="success" onClick={recordPayment}>✅ Enregistrer le paiement</Button></>}>
          {selected && <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: "#f0f9ff", textAlign: "center" }}>
                <Typography color="text.secondary">💵 Total facture</Typography>
                <Typography variant="h5" fontWeight={700}>{formatNumber(selected.achatTTC)} TND</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper sx={{ p: 2, textAlign: "center" }}>
                <Typography color="text.secondary">✅ Déjà payé</Typography>
                <Typography variant="h6" sx={{ color: "#10b981" }}>{formatNumber(selected.montantPaye)} TND</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper sx={{ p: 2, textAlign: "center" }}>
                <Typography color="text.secondary">⚠️ Reste à payer</Typography>
                <Typography variant="h6" sx={{ color: parseNumber(selected.resteAPayer) > 0 ? "#ef4444" : "#10b981" }}>{formatNumber(selected.resteAPayer)} TND</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="💰 Montant à payer" 
                type="number" 
                value={paymentForm.montantPaye} 
                onChange={(e) => setPaymentForm({ ...paymentForm, montantPaye: e.target.value })} 
                inputProps={{ max: parseNumber(selected.resteAPayer), min: 0, step: "0.001" }} 
                helperText={`Maximum: ${formatNumber(selected.resteAPayer)} TND`}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>💳 Mode de paiement</InputLabel>
                <Select value={paymentForm.modeDePaiement} onChange={(e) => setPaymentForm({ ...paymentForm, modeDePaiement: e.target.value })}>
                  <MenuItem value="cash">💵 Espèces</MenuItem>
                  <MenuItem value="check">📝 Chèque</MenuItem>
                  <MenuItem value="transfer">🏦 Virement</MenuItem>
                  <MenuItem value="card">💳 Carte</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="🔢 Référence" value={paymentForm.paymentnum} onChange={(e) => setPaymentForm({ ...paymentForm, paymentnum: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <DatePicker label="📅 Date paiement" value={paymentForm.datePaiement} onChange={(d) => setPaymentForm({ ...paymentForm, datePaiement: d })} renderInput={(p) => <TextField {...p} fullWidth />} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="📝 Notes" value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} />
            </Grid>
          </Grid>}
        </StyledDialog>
        
        {/* Supplier Dialog */}
        <StyledDialog open={openSupplier} onClose={() => { setOpenSupplier(false); setSupplierEdit(null); setSupplierForm({ name: "", email: "", numero: "", address: "", taxId: "", paymentTerms: "30_days", retenueSource: { enabled: false, taux: 0 }, listDeProduit: [] }); }} title={supplierEdit ? "✏️ Modifier fournisseur" : "🏢 Nouveau fournisseur"} icon={Business} maxWidth="sm"
          actions={<><Button onClick={() => setOpenSupplier(false)}>Annuler</Button><Button variant="contained" onClick={saveSupplier} startIcon={<Save />}>💾 Enregistrer</Button></>}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InfoAlert 
                message="💡 Le fournisseur doit être enregistré avant de pouvoir créer une facture. Après création, il sera automatiquement sélectionné."
                severity="info"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="🏢 Nom du fournisseur *" value={supplierForm.name} onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="📧 Email" type="email" value={supplierForm.email} onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="📞 Téléphone" value={supplierForm.numero} onChange={(e) => setSupplierForm({ ...supplierForm, numero: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="📍 Adresse" value={supplierForm.address} onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="🔢 N° TVA" value={supplierForm.taxId} onChange={(e) => setSupplierForm({ ...supplierForm, taxId: e.target.value })} helperText="Optionnel" />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>⏱️ Conditions de paiement</InputLabel>
                <Select value={supplierForm.paymentTerms} onChange={(e) => setSupplierForm({ ...supplierForm, paymentTerms: e.target.value })}>
                  <MenuItem value="immediate">Paiement immédiat</MenuItem>
                  <MenuItem value="7_days">7 jours</MenuItem>
                  <MenuItem value="15_days">15 jours</MenuItem>
                  <MenuItem value="30_days">30 jours (Standard)</MenuItem>
                  <MenuItem value="45_days">45 jours</MenuItem>
                  <MenuItem value="60_days">60 jours</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: "#fef3c7" }}>
                <FormControlLabel 
                  control={<Checkbox checked={supplierForm.retenueSource?.enabled} onChange={(e) => setSupplierForm({ ...supplierForm, retenueSource: { ...supplierForm.retenueSource, enabled: e.target.checked, taux: supplierForm.retenueSource?.taux || 0 } })} />} 
                  label="🔖 Appliquer la retenue à la source" 
                />
                {supplierForm.retenueSource?.enabled && (
                  <TextField 
                    type="number" 
                    label="Taux (%)" 
                    value={supplierForm.retenueSource?.taux || 0} 
                    onChange={(e) => setSupplierForm({ ...supplierForm, retenueSource: { ...supplierForm.retenueSource, taux: parseFloat(e.target.value) || 0 } })} 
                    sx={{ mt: 1 }} 
                    fullWidth 
                    helperText="Pourcentage à déduire automatiquement sur chaque facture"
                    inputProps={{ step: "0.1" }}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>
        </StyledDialog>
        
        {/* Hints Popover */}
        <Popover
          open={Boolean(hintAnchor)}
          anchorEl={hintAnchor}
          onClose={() => setHintAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ p: 2, maxWidth: 300 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>💡 Astuces</Typography>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2" sx={{ mb: 1 }}>
              • 📝 Pour créer un nouvel ingrédient, tapez son nom dans le champ produit et sélectionnez "Créer"
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • 🏢 Ajoutez d'abord vos fournisseurs avant de créer des factures
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • 💰 Le prix des ingrédients est mis à jour automatiquement (moyenne pondérée avec 3 décimales)
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • 📊 Le prix de revient des plats est recalculé automatiquement
            </Typography>
            <Typography variant="body2">
              • 📥 Exportez vos données en Excel pour l'analyse
            </Typography>
          </Box>
        </Popover>
        
        {/* Confirm Dialog */}
        <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
          <Box sx={{ p: 3, minWidth: 350 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Avatar sx={{ bgcolor: "#ef4444" }}><WarningIcon /></Avatar>
              <Typography variant="h6">{confirmAction?.title}</Typography>
            </Box>
            <Typography sx={{ my: 2 }}>{confirmAction?.message}</Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button onClick={() => setOpenConfirm(false)} variant="outlined">Annuler</Button>
              <Button variant="contained" color="error" onClick={confirmAction?.onConfirm}>Confirmer</Button>
            </Box>
          </Box>
        </Dialog>
        
        {/* Snackbar */}
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={8000} 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Paper sx={{ p: 2, display: "flex", alignItems: "flex-start", gap: 2, borderLeft: `4px solid ${snackbar.severity === "success" ? "#10b981" : snackbar.severity === "error" ? "#ef4444" : "#f59e0b"}`, maxWidth: 500, whiteSpace: "pre-line" }}>
            {snackbar.severity === "success" ? <CheckCircle sx={{ color: "#10b981" }} /> : snackbar.severity === "error" ? <Error sx={{ color: "#ef4444" }} /> : <WarningIcon sx={{ color: "#f59e0b" }} />}
            <Typography variant="body2" sx={{ flex: 1, whiteSpace: "pre-line" }}>{snackbar.message}</Typography>
            <IconButton size="small" onClick={() => setSnackbar({ ...snackbar, open: false })}><Close fontSize="small" /></IconButton>
          </Paper>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default FactureScreen;